import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { API_BASE_URL } from './api.config';
import { unwrapPagedList } from './paged-list';
import { TruckAnnouncement, TruckAnnouncementListItem, TruckAnnouncementPin, TruckAnnouncementPinListItem, TruckAnnouncementSearchRequest } from '../../shared/models/truck-announcement';

@Injectable({ providedIn: 'root' })
export class TruckAnnouncementService {
  constructor(private readonly http: HttpClient) {}

  search(request: TruckAnnouncementSearchRequest = { Page: 1, PageSize: 50 }) {
    return this.http.post<unknown>(`${API_BASE_URL}/mobile/TruckAnnouncementListSearch`, request).pipe(
      map(raw => unwrapPagedList<TruckAnnouncementListItem>(raw))
    );
  }

  get(id: string | number) {
    return this.http.get<TruckAnnouncement>(`${API_BASE_URL}/mobile/GetTruckAnnouncement/${encodeURIComponent(String(id))}`);
  }

  add(model: TruckAnnouncement) {
    return this.http.post<unknown>(`${API_BASE_URL}/mobile/AddTruckAnnouncement`, model);
  }

  edit(model: TruckAnnouncement) {
    return this.http.post<unknown>(`${API_BASE_URL}/mobile/EditTruckAnnouncement`, model);
  }

  send(id: string | number) {
    // Endpoint name confirmed against the real app's compiled Dart binary
    // (literal strings "sendTruckAnnouncement" / "/mobile/SendTruckAnnouncement/").
    // The earlier POST attempt failed with "does not support http method
    // 'POST'" — ASP.NET Web API's standard reply when the route exists but
    // needs a different verb — so this is a GET action.
    return this.http.get<unknown>(`${API_BASE_URL}/mobile/SendTruckAnnouncement/${encodeURIComponent(String(id))}`);
  }

  searchPins(request: TruckAnnouncementSearchRequest = { Page: 1, PageSize: 50 }) {
    return this.http.post<unknown>(`${API_BASE_URL}/mobile/TruckAnnouncementPinListSearch`, request).pipe(
      map(raw => unwrapPagedList<TruckAnnouncementPinListItem>(raw))
    );
  }

  getPin(id: string | number) {
    return this.http.get<TruckAnnouncementPin>(`${API_BASE_URL}/mobile/GetTruckAnnouncementPin/${encodeURIComponent(String(id))}`);
  }

  editPin(pin: TruckAnnouncementPin) {
    return this.http.post<unknown>(`${API_BASE_URL}/mobile/EditTruckAnnouncementPin`, pin);
  }

  extractId(raw: unknown): number | null {
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) return Number(raw);
    if (!raw || typeof raw !== 'object') return null;
    const obj = raw as Record<string, unknown>;
    if (typeof obj['Id'] === 'number') return obj['Id'] as number;
    return null;
  }

  toApiDate(value: string | null | undefined): string | undefined {
    if (!value) return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toISOString();
  }

  toLocalInputDate(value: string | null | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.slice(0, 16);
    const offset = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
  }
}
